'use client';

import { IUITile } from '@/src/types/ITile';
import { useEditingHoverControl } from '../../../hooks/useEditingHoverControl';
import { Icon, useToast } from '@/src/components/ui';
import { useClickOrDrag } from '../../../hooks/useClickOrDrag';
import { createTile, editTile } from '@/src/actions/tiles';
import { Textarea } from '@workspace/ui/components/textarea';
import { Input } from '@workspace/ui/components/input';
import { cn } from '@workspace/ui/lib/utils';
import { uploadImage } from '@/src/actions/storage';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useTilesStore } from '../../../store/tiles-store';
import { useBoolean } from '@/src/hooks/use-boolean';
import { noBorderStyles } from '@/src/constants/constants';
import { LoadingOverlay } from '../LoadingOverlay';
import { Button } from '@workspace/ui/components/button';

type ThumbnailUploaderProps = {
  thumbnail: string | null;
  icon: string | null;
  isOpenLink: boolean;
  fileInputRef: RefObject<HTMLInputElement> | any;
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  className?: any;
};

const ThumbnailUploader = ({
  thumbnail,
  icon,
  isOpenLink,
  fileInputRef,
  handleFileChange,
  className,
}: ThumbnailUploaderProps) => (
  <>
    <Input
      id="file-upload"
      type="file"
      ref={fileInputRef}
      className="hidden w-full h-full"
      accept="image/*"
      onChange={handleFileChange}
    />
    {thumbnail || icon ? (
      <div className={cn('relative w-full h-full', className)}>
        <img
          src={thumbnail || icon || ''}
          alt="Thumbnail"
          className="w-full h-full object-cover rounded-2xl"
        />
        <label
          htmlFor="file-upload"
          className="absolute w-full h-full inset-0 flex items-center justify-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity duration-200 cursor-pointer"
          aria-label="Change thumbnail"
        >
          <Icon name="change" className="size-10 text-white" />
        </label>
      </div>
    ) : (
      <label
        htmlFor="file-upload"
        className="flex flex-col items-center w-full justify-center size-24 cursor-pointer gap-2"
        aria-label="Upload thumbnail"
      >
        <div className="text-sm text-center font-semibold text-black/40 w-full">
          Add thumbnail
        </div>
        <Icon
          name="imageView"
          width={17}
          height={13}
          className="text-black/40"
        />
      </label>
    )}
  </>
);

type LinkInputProps = {
  linkUrl: string;
  setLinkUrl: (url: string) => void;
  isOpenLink: boolean;
  toggleLink: () => void;
  isMobileView: boolean;
};

export const LinkEditor = ({
  linkUrl,
  setLinkUrl,
  isOpenLink,
  toggleLink,
  isMobileView,
}: LinkInputProps) => (
  <>
    <button
      onClick={toggleLink}
      className={cn(
        'absolute size-8 rounded-full flex items-center justify-center transition-all duration-200 z-20',
        {
          'bg-black text-white left-2 bottom-2': isOpenLink,
          'bg-black right-2 bottom-2 hover:bg-black text-white':
            !isOpenLink,
        }
      )}
    >
      <Icon
        name="link-2"
        className={cn('size-4 cursor-pointer', {
          'text-white': isOpenLink,
          'text-black/40': !isOpenLink && !linkUrl,
        })}
      />
    </button>

    {isOpenLink && (
      <Input
        type="text"
        placeholder="Paste link"
        className={cn(
          'absolute bottom-2 left-2 md:h-8 w-[calc(100%-1rem)] pl-10 bg-white text-black rounded-full placeholder:text-[#D5D7DA] placeholder:text-xs'
        )}
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
      />
    )}
  </>
);

const LinkPreview = ({
  url,
  onClick,
  className = '',
}: {
  url: string;
  onClick?: () => void;
  className?: any;
}) => (
  <a
    href={url}
    onMouseDown={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick?.();
    }}
    target="_blank"
    className={cn(
      'size-8 rounded-full flex items-center justify-center transition-all duration-200 z-20',
      {
        'bg-[#4285F4] text-white': url,
        'bg-white hover:bg-black hover:text-white': !url,
      },
      className
    )}
  >
    <Icon name="link-2" className="size-4 cursor-pointer text-white" />
  </a>
);

const SaveConfirmationOverlay = ({
  onSave,
  onCancel,
  isLoading = false,
}: {
  onSave: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}) => (
  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-2xl z-50">
    <div className="bg-white p-4 rounded-xl flex flex-col gap-3 min-w-48">
      <p className="text-sm font-medium text-center">Save changes?</p>
      <div className="flex gap-2 justify-center">
        <Button
          size="sm"
          onClick={onSave}
          disabled={isLoading}
          className="p-1.5 w-fit h-fit bg-blue-primary hover:bg-blue-primary/90"
        >
          <Icon name="done" className="md:size-5 size-4" />
        </Button>
        <Button
          size="sm"
          onClick={onCancel}
          disabled={isLoading}
          className="p-1 size-7 bg-red-error hover:bg-red-error/90 flex items-center justify-center"
        >
          <Icon name="close" className="md:size-4 size-3 text-white" />
        </Button>
      </div>
    </div>
  </div>
);

const EditingControls = ({
  hasUnsavedChanges,
  onSave,
  onCancel,
  isSmallTile,
  isLoading = false,
}: {
  hasUnsavedChanges: boolean;
  onSave: () => void;
  onCancel: () => void;
  isSmallTile: boolean;
  isLoading?: boolean;
}) => {
  if (!hasUnsavedChanges) return null;

  return (
    <div
      className={cn(
        'absolute flex gap-1 z-30',
        isSmallTile ? 'top-1 right-1' : 'top-2 right-2'
      )}
    >
      <Button
        size="sm"
        onClick={onSave}
        disabled={isLoading}
        className="p-1 w-fit h-fit bg-blue-primary hover:bg-blue-primary/90"
      >
        <Icon name="done" className="md:size-5 size-5" />
      </Button>
      <Button
        size="sm"
        onClick={onCancel}
        disabled={isLoading}
        className="p-1 size-7 bg-red-error hover:bg-red-error/90 flex items-center justify-center"
      >
        <Icon name="close" className="md:size-4 size-3 text-white" />
      </Button>
    </div>
  );
};

type Props = {
  onHoverStateChange: (shouldDisable: boolean) => void;
  isMyProfile: boolean;
  tile: IUITile;
  touchedTileId: string | null;
  setTouchedTileId: (id: string | null) => void;
  isMobileView: boolean;
};

export const CustomTile = ({
  isMyProfile,
  tile,
  onHoverStateChange,
  touchedTileId,
  setTouchedTileId,
  isMobileView,
}: Props) => {
  const { handleStartEditing, handleStopEditing } =
    useEditingHoverControl(onHoverStateChange);
  const updateTile = useTilesStore((state) => state.updateTile);
  const setTileLoading = useTilesStore((state) => state.setTileLoading);

  const toast = useToast();

  const tileRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isOpenLink = useBoolean(true);
  const isStartEditing = useBoolean(false);

  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const is2x2 = isMobileView
    ? tile.mw === 2 && tile.mh === 6
    : tile.w === 2 && tile.h === 6;
  const is1x2 = isMobileView
    ? tile.mw === 1 && tile.mh === 6
    : tile.w === 1 && tile.h === 6;
  const is2x1 = isMobileView
    ? tile.mw === 2 && tile.mh === 3
    : tile.w === 2 && tile.h === 3;
  const is1x1 = isMobileView
    ? tile.mw === 1 && tile.mh === 3
    : tile.w === 1 && tile.h === 3;

  const handleStartEdit = () => {
    updateTile(tile.i, { isEditing: true });
    isStartEditing.onTrue();
    handleStartEditing();
  };

  const { onMouseDown, onTouchStart } = useClickOrDrag(handleStartEdit);

  const [thumbnail, setThumbnail] = useState<string | null>(tile.icon ?? null);
  const [linkUrl, setLinkUrl] = useState(tile.content ?? '');
  const [linkDescription, setLinkDescription] = useState(tile.title ?? '');

  useEffect(() => {
    const hasChanges =
      thumbnail !== tile.iconName ||
      linkUrl !== tile.content ||
      linkDescription !== tile.title;

    setHasUnsavedChanges(hasChanges);
  }, [
    thumbnail,
    linkUrl,
    linkDescription,
    tile.iconName,
    tile.content,
    tile.title,
  ]);

  const validateFields = (
    thumbnail: string | null,
    tile: IUITile,
    linkUrl: string,
    linkDescription: string,
    toast: ReturnType<typeof useToast>
  ): boolean => {
    if (!thumbnail && !tile.iconName) {
      toast.error('Thumbnail required in custom tile');
      return false;
    }
    if (!linkUrl || !/^https:\/\/.+/.test(linkUrl)) {
      toast.error('Invalid link in custom tile');
      return false;
    }
    if (!linkDescription.trim()) {
      toast.error('Title required in custom tile');
      return false;
    }
    return true;
  };

  const trySave = async () => {
    if (!validateFields(thumbnail, tile, linkUrl, linkDescription, toast)) {
      return;
    }

    setTileLoading(tile.i, true);
    setTouchedTileId(null);
    setShowSaveConfirmation(false);

    try {
      const payload = {
        content: linkUrl,
        title: linkDescription,
        icon: thumbnail || tile.iconName,
      };

      const response =
        tile.content && tile.id
          ? await editTile(payload, tile.id)
          : await createTile({ ...tile, ...payload });

      if (!response) {
        toast.error('Failed to save tile');
        return;
      }

      updateTile(tile.i, {
        ...tile,
        ...response,
        icon: response.icon || thumbnail,
        static: false,
        isEditing: false,
      });

      toast.updated('Saved');
      handleStopEditing();
      isStartEditing.onFalse();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving tile:', error);
      toast.error('Failed to save tile');
    } finally {
      setTileLoading(tile.i, false);
    }
  };

  const handleCancel = () => {
    setThumbnail(tile.iconName ?? null);
    setLinkUrl(tile.content ?? '');
    setLinkDescription(tile.title ?? '');
    setHasUnsavedChanges(false);
    setShowSaveConfirmation(false);

    if (isStartEditing.value) {
      updateTile(tile.i, { isEditing: false });
      isStartEditing.onFalse();
      handleStopEditing();
    }
  };

  const handleManualSave = () => {
    if (!hasUnsavedChanges) return;
    trySave();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || file.size > 4 * 1024 * 1024) return;

    const { data, error } = await uploadImage({ blob: file, type: 'tile' });
    if (error || !data?.url) {
      toast.error('Failed to upload thumbnail');
      return;
    }

    setThumbnail(data.url);
    updateTile(tile.i, {
      ...tile,
      static: false,
      icon: data.url,
    });
    handleStartEditing();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tileRef.current && !tileRef.current.contains(event.target as Node)) {
        if (hasUnsavedChanges && isStartEditing.value) {
          setShowSaveConfirmation(true);
        } else if (isStartEditing.value) {
          handleCancel();
        }
      }
    };

    if (isStartEditing.value) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [hasUnsavedChanges, isStartEditing.value]);

  if (isMyProfile && isStartEditing.value) {
    const isSmallTile = is1x1 || is2x1;

    return (
      <div
        ref={tileRef}
        className={cn(
          'w-full h-full border rounded-2xl p-2 text-gray-text cursor-pointer transition-colors relative',
          isSmallTile
            ? 'flex flex-col items-center justify-end'
            : 'relative flex flex-col items-center'
        )}
      >
        {tile.isLoading && <LoadingOverlay />}

        {showSaveConfirmation && (
          <SaveConfirmationOverlay
            onSave={trySave}
            onCancel={() => {
              setShowSaveConfirmation(false);
              handleCancel();
            }}
            isLoading={tile.isLoading}
          />
        )}

        <EditingControls
          hasUnsavedChanges={hasUnsavedChanges}
          onSave={handleManualSave}
          onCancel={handleCancel}
          isSmallTile={isSmallTile}
          isLoading={tile.isLoading}
        />

        <div
          className={cn(
            'relative overflow-hidden rounded-2xl border bg-bg-gray',
            isSmallTile
              ? 'size-28 flex justify-center items-center'
              : 'w-full flex justify-center items-center'
          )}
        >
          <ThumbnailUploader
            thumbnail={thumbnail}
            icon={tile.iconName ?? null}
            isOpenLink={isOpenLink.value}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            className={cn({
              'h-80': is2x2,
            })}
          />
        </div>

        <Textarea
          style={noBorderStyles}
          value={linkDescription}
          maxLength={70}
          onChange={(e) => setLinkDescription(e.target.value)}
          placeholder="Add title"
          className={cn(
            'w-full text-black font-medium resize-none py-2 tracking-tighter placeholder:font-medium placeholder:text-gray-text text-start text-xs',
            { 'md:text-base': !isMobileView },
            isSmallTile ? 'h-1/2 text-center' : is1x2 ? 'h-fit' : 'h-24'
          )}
        />

        {/* {isSmallTile ? ( */}
          <LinkEditor
            linkUrl={linkUrl}
            setLinkUrl={setLinkUrl}
            isOpenLink={isOpenLink.value}
            toggleLink={isOpenLink.onToggle}
            isMobileView={isMobileView}
          />
        {/* ) : ( */}
          {/* <>
            <button
              onClick={isOpenLink.onToggle}
              className={cn(
                'absolute bottom-2 left-2 size-8 rounded-full flex items-center justify-center transition-all duration-200 z-20',
                {
                  'bg-[#4285F4] text-white': linkUrl,
                  'bg-black text-white': isOpenLink.value,
                }
              )}
            >
              <Icon
                name="link-2"
                className="size-4 text-white cursor-pointer"
              />
            </button>
            <Input
              type="text"
              placeholder="Paste link"
              className={cn(
                'absolute bottom-2 md:right-2 h-8 md:pl-10 pl-8 bg-white text-black rounded-full placeholder:text-[#D5D7DA] placeholder:text-xs custom-shadow text-xs md:text-base',
                {
                  'w-96': is2x2,
                  'w-44': is1x2,
                }
              )}
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
            />
          </>
        )} */}
      </div>
    );
  }

  const ViewLinkIcon = (
    <LinkPreview
      url={tile.content || ''}
      onClick={isStartEditing.onFalse}
      className={cn({
        'absolute top-4 right-4': is2x2,
        'absolute bottom-2': is1x2,
        'top-4 right-4': is1x1,
        'top-2 right-2': (is1x1 || is2x1) && isMobileView,
      })}
    />
  );

  const Title = (
    <div
      className={cn('font-medium text-xs flex items-start w-full break-all', {
        'md:text-sm': !isMobileView,
      })}
    >
      {tile.title}
    </div>
  );

  if (is2x2) {
    return (
      <div
        onMouseDown={isMyProfile ? onMouseDown : undefined}
        onTouchStart={(e) => {
          if ((e.target as HTMLElement).closest('[data-link]')) return;
          if (isMyProfile) {
            if (touchedTileId !== tile.i) {
              e.preventDefault();
              return;
            }
            onTouchStart(e);
          }
        }}
        className="relative h-full w-full rounded-2xl overflow-hidden"
        style={
          tile.iconName
            ? {
                backgroundImage: `linear-gradient(to top, rgba(0, 0, 0, 0.6), transparent), url(${tile.iconName})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { backgroundColor: '#e5e7eb' }
        }
      >
        {ViewLinkIcon}
        {tile.title && (
          <div
            className={cn(
              'absolute bottom-4 left-4 text-white font-medium text-xl'
            )}
          >
            {tile.title}
          </div>
        )}
      </div>
    );
  }

  if (is1x2) {
    return (
      <div
        onMouseDown={onMouseDown}
        onTouchStart={(e) => {
          if (isMyProfile) {
            if (touchedTileId !== tile.i) {
              e.preventDefault();
              return;
            }
            onTouchStart(e);
          }
        }}
        className="flex flex-col h-full w-full p-2 gap-3 justify-between"
      >
        <div className="flex flex-col gap-3">
          {tile.iconName && (
            <div className="w-full h-48">
              <img
                src={tile.iconName}
                alt="Thumbnail"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          )}
          {Title}
        </div>
        {ViewLinkIcon}
      </div>
    );
  }

  if (is2x1) {
    return (
      <div
        onMouseDown={onMouseDown}
        onTouchStart={(e) => {
          if (isMyProfile) {
            if (touchedTileId !== tile.i) {
              e.preventDefault();
              return;
            }
            onTouchStart(e);
          }
        }}
        className="flex h-full w-full p-2 gap-3"
      >
        {tile.iconName && (
          <div
            className={cn(' w-40', {
              'md:size-[180px] md:w-1/2': !isMobileView,
            })}
          >
            <img
              src={tile.iconName}
              alt="Thumbnail"
              className={cn('w-full h-full min-w-40 object-cover rounded-2xl', {
                'md:min-w-48': !isMobileView,
              })}
            />
          </div>
        )}
        <div className="flex flex-col justify-between items-end w-full">
          {ViewLinkIcon}
          {Title}
        </div>
      </div>
    );
  }

  if (tile.static && !isStartEditing.value) {
    return (
      <div
        className="p-2 md:text-base text-xs font-light h-full w-full text-start"
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleStartEdit();
        }}
      >
        <div className="flex flex-col items-center justify-center bg-bg-gray rounded-2xl border">
          <ThumbnailUploader
            thumbnail={tile.iconName ?? null}
            icon={tile.iconName ?? null}
            isOpenLink={isOpenLink.value}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange}
            className="w-full h-48 mb-2"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseDown={onMouseDown}
      onTouchStart={(e) => {
        if (isMyProfile) {
          if (touchedTileId !== tile.i) {
            e.preventDefault();
            return;
          }
          onTouchStart(e);
          isStartEditing.onTrue();
        }
      }}
      className={cn('flex flex-col h-full w-full p-2 justify-between', {})}
    >
      <div className="flex justify-between w-full items-end">
        {tile.iconName && (
          <div className="md:size-24 size-20">
            <img
              src={tile.iconName}
              alt="Thumbnail"
              className="w-full h-full object-cover rounded-2xl"
            />
          </div>
        )}
        {ViewLinkIcon}
      </div>
      {Title}
    </div>
  );
};
